import {
  type DocumentReference,
  type TypeGuardFn,
  isDocRef,
  isObject,
  TypeGuard,
} from '@principle-theorem/shared';
import { isArray, isBoolean, sumBy } from 'lodash';
import { type IPractice } from '../../practice/practice';
import {
  KNOWLEDGE_BASE_MAX_COLLECTION_ITEMS,
  KNOWLEDGE_BASE_MAX_CUSTOM_SECTION_TITLE_LENGTH,
  KNOWLEDGE_BASE_MAX_QA_ANSWER_LENGTH,
  KNOWLEDGE_BASE_MAX_QA_QUESTION_LENGTH,
} from '../ovie/knowledge-base/knowledge-base-push-body';
import {
  type IImportProposal,
  IMPORT_PROPOSAL_SECTION_QA_PAIR_KEYS,
  type SectionQaPairLists,
} from './import-proposal';
import { type IWebsiteSource } from './website-snapshot';

/**
 * Why a Website Import produced no Import Proposal. The review shows a
 * different explanation for each, so the practice knows whether to fix the
 * address or simply try again.
 */
export enum ImportFailureReason {
  FetchFailed = 'fetch-failed',
  ExtractionFailed = 'extraction-failed',
  NoUsableContent = 'no-usable-content',
}

/**
 * An existing Knowledge Base Q&A pair as sent with a Website Import, so the
 * extractor can offer an update to it rather than ask the same question in
 * other words.
 */
export interface IImportExistingQaPair {
  id: string;
  question: string;
  /**
   * The answer the receptionist gives now, so the extractor can tell whether
   * the website says anything it does not. It may be shortened, and it is
   * blank when it did not fit within `IMPORT_EXISTING_CONTENT_MAX_CHARS`.
   */
  answer: string;
}

/**
 * An existing custom section as sent with a Website Import. A proposal refers
 * to it by its position in `IImportExistingContent.customSections`, which is
 * also its position in the Knowledge Base.
 */
export interface IImportExistingCustomSection {
  title: string;
  /**
   * The section's content, so the extractor can tell whether the website
   * says anything it does not. It may be shortened, and it is blank when it
   * did not fit within `IMPORT_EXISTING_CONTENT_MAX_CHARS`.
   */
  content: string;
}

/**
 * What the Knowledge Base already covers, sent with a Website Import so that a
 * proposal for something the practice already has is offered as an update to
 * it instead of as a reworded duplicate.
 * `ImportExistingContent.existingContentOf` builds it from the editor's
 * unsaved Knowledge Base within the bounds `isImportFromWebsiteRequest`
 * accepts.
 */
export interface IImportExistingContent {
  sectionQaPairs: SectionQaPairLists<IImportExistingQaPair>;
  customSections: IImportExistingCustomSection[];
}

export interface IImportFromWebsiteRequest {
  practiceRef: DocumentReference<IPractice>;
  websiteUrl: string;
  useSnapshot: boolean;
  existingContent: IImportExistingContent;
}

export enum ImportFromWebsiteStatus {
  Ok = 'ok',
  Empty = 'empty',
}

export interface IImportFromWebsiteOkResponse {
  status: ImportFromWebsiteStatus.Ok;
  proposal: IImportProposal;
}

export interface IImportFromWebsiteEmptyResponse {
  status: ImportFromWebsiteStatus.Empty;
  reason: ImportFailureReason;
  source: IWebsiteSource;
  pagesRequested: number;
  pagesRead: number;
}

export type IImportFromWebsiteResponse =
  | IImportFromWebsiteOkResponse
  | IImportFromWebsiteEmptyResponse;

/**
 * A clinician as described to the team overview generator. The roster is
 * gathered in the browser and sent to the server, which owns the prompt.
 */
export interface IKnowledgeBaseClinicianBio {
  name: string;
  bio?: string;
  specialty?: string;
}

export interface IGenerateTeamOverviewRequest {
  practiceRef: DocumentReference<IPractice>;
  practiceName: string;
  clinicians: IKnowledgeBaseClinicianBio[];
}

export interface IGenerateTeamOverviewResponse {
  overview: string;
}

/**
 * The longest existing Q&A pair id a Website Import request may send. Ids are
 * generated UUIDs, so this only limits what a caller can put in the prompt.
 */
export const IMPORT_EXISTING_QA_PAIR_ID_MAX_LENGTH = 100;
/**
 * The longest existing custom section content a Website Import request may
 * send. Its opening is enough for the extractor to judge whether the website
 * says more, and a long section costs no more than this in the prompt.
 */
export const IMPORT_EXISTING_CUSTOM_SECTION_CONTENT_MAX_LENGTH = 2000;
/**
 * The most existing text one Website Import request may send: the lengths of
 * every existing question, answer, custom section title and content added
 * together. Ids are bounded on their own. It keeps the extraction prompt,
 * which the server pays for, small even for the largest Knowledge Base.
 */
export const IMPORT_EXISTING_CONTENT_MAX_CHARS = 60_000;

const websiteUrlMaxLength = 2048;
const teamOverviewMaxClinicians = 100;
const teamOverviewMaxNameLength = 200;
const teamOverviewMaxDetailLength = 2000;
const teamOverviewMaxPracticeNameLength = 200;

function isStringWithinLength(maxLength: number): TypeGuardFn<string> {
  return (data): data is string =>
    TypeGuard.isString(data) && data.length <= maxLength;
}

function isArrayWithinLength<T>(
  guardFn: TypeGuardFn<T>,
  maxLength: number
): TypeGuardFn<T[]> {
  return (data): data is T[] =>
    isArray(data) &&
    data.length <= maxLength &&
    TypeGuard.arrayOf(guardFn)(data);
}

/**
 * Adds up the existing text `IMPORT_EXISTING_CONTENT_MAX_CHARS` limits: every
 * question, answer, custom section title and content.
 */
function existingContentLength(
  existingContent: IImportExistingContent
): number {
  const qaPairLength = sumBy(
    Object.values(existingContent.sectionQaPairs).flatMap(
      (pairs) => pairs ?? []
    ),
    (pair) => pair.question.length + pair.answer.length
  );
  const customSectionLength = sumBy(
    existingContent.customSections,
    (section) => section.title.length + section.content.length
  );
  return qaPairLength + customSectionLength;
}

const isImportExistingQaPairList = isArrayWithinLength(
  TypeGuard.interface<IImportExistingQaPair>({
    id: (data: unknown): data is string =>
      TypeGuard.isString(data) &&
      data.length > 0 &&
      data.length <= IMPORT_EXISTING_QA_PAIR_ID_MAX_LENGTH,
    question: isStringWithinLength(KNOWLEDGE_BASE_MAX_QA_QUESTION_LENGTH),
    answer: isStringWithinLength(KNOWLEDGE_BASE_MAX_QA_ANSWER_LENGTH),
  }),
  KNOWLEDGE_BASE_MAX_COLLECTION_ITEMS
);

/**
 * A Knowledge Base holds at most the provider's collection cap of Q&A pairs
 * per section and of custom sections, so a list longer than that is not one
 * the editor could have sent.
 */
const isImportExistingContentWithinItemBounds =
  TypeGuard.interface<IImportExistingContent>({
    sectionQaPairs: (
      data: unknown
    ): data is SectionQaPairLists<IImportExistingQaPair> =>
      isObject(data) &&
      !isArray(data) &&
      Object.entries(data).every(
        ([section, pairs]) =>
          IMPORT_PROPOSAL_SECTION_QA_PAIR_KEYS.some((key) => key === section) &&
          isImportExistingQaPairList(pairs)
      ),
    customSections: isArrayWithinLength(
      TypeGuard.interface<IImportExistingCustomSection>({
        title: isStringWithinLength(
          KNOWLEDGE_BASE_MAX_CUSTOM_SECTION_TITLE_LENGTH
        ),
        content: isStringWithinLength(
          IMPORT_EXISTING_CUSTOM_SECTION_CONTENT_MAX_LENGTH
        ),
      }),
      KNOWLEDGE_BASE_MAX_COLLECTION_ITEMS
    ),
  });

/**
 * Every item can be within its own bounds while all of them together still
 * make a large prompt, so the text as a whole is bounded too.
 */
const isImportExistingContent = TypeGuard.merge(
  isImportExistingContentWithinItemBounds,
  (data: IImportExistingContent): data is IImportExistingContent =>
    existingContentLength(data) <= IMPORT_EXISTING_CONTENT_MAX_CHARS
);

const isKnowledgeBaseClinicianBio =
  TypeGuard.interface<IKnowledgeBaseClinicianBio>({
    name: isStringWithinLength(teamOverviewMaxNameLength),
    // The callable transport encodes an undefined property as null
    bio: TypeGuard.nilOr(isStringWithinLength(teamOverviewMaxDetailLength)),
    specialty: TypeGuard.nilOr(
      isStringWithinLength(teamOverviewMaxDetailLength)
    ),
  });

/**
 * Bounds the existing content as well as the address because both reach the
 * extraction prompt, which the server pays for and any practice user can call.
 */
export const isImportFromWebsiteRequest =
  TypeGuard.interface<IImportFromWebsiteRequest>({
    practiceRef: isDocRef,
    websiteUrl: isStringWithinLength(websiteUrlMaxLength),
    useSnapshot: isBoolean,
    existingContent: isImportExistingContent,
  });

/**
 * Bounds every field because the generation is paid for by the server and any
 * practice user can call it.
 */
export const isGenerateTeamOverviewRequest =
  TypeGuard.interface<IGenerateTeamOverviewRequest>({
    practiceRef: isDocRef,
    practiceName: isStringWithinLength(teamOverviewMaxPracticeNameLength),
    clinicians: (data: unknown): data is IKnowledgeBaseClinicianBio[] =>
      isArray(data) &&
      data.length <= teamOverviewMaxClinicians &&
      data.every(isKnowledgeBaseClinicianBio),
  });
